import { useState } from "react";
import { motion } from "framer-motion";
import { 
  Search,
  Rocket,
  FileEdit,
  Share2,
  BarChart3,
  CreditCard,
  AlertTriangle,
  ChevronRight,
  BookOpen,
  MessageCircle,
  Mail
} from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Section } from "@/components/layout/Section";
import { Container } from "@/components/layout/Container";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { AnimatedSection } from "@/components/ui/animated-section";
import { staggerContainer, staggerItem } from "@/lib/animations";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

// Help categories with articles
const helpCategories = [
  {
    id: "getting-started",
    icon: Rocket,
    title: "Getting Started",
    description: "New to SurviAI? Start here to learn the basics.",
    articles: [
      { title: "Creating your first survey", slug: "first-survey" },
      { title: "Understanding the dashboard", slug: "dashboard-overview" },
      { title: "Uploading your first document", slug: "upload-document" },
      { title: "Account setup and preferences", slug: "account-setup" },
    ],
  },
  {
    id: "survey-creation",
    icon: FileEdit,
    title: "Survey Creation",
    description: "Learn how to create, edit, and customize your surveys.",
    articles: [
      { title: "Supported document formats", slug: "supported-formats" },
      { title: "AI question extraction explained", slug: "ai-extraction" },
      { title: "Editing extracted questions", slug: "edit-questions" },
      { title: "Adding custom questions", slug: "add-questions" },
      { title: "Question types overview", slug: "question-types" },
    ],
  },
  {
    id: "distribution",
    icon: Share2,
    title: "Distribution",
    description: "Share your surveys and collect responses effectively.",
    articles: [
      { title: "Sharing via link", slug: "share-link" },
      { title: "Embedding surveys on websites", slug: "embed-survey" },
      { title: "Email distribution", slug: "email-distribution" },
      { title: "Managing survey access", slug: "survey-access" },
    ],
  },
  {
    id: "responses-analytics",
    icon: BarChart3,
    title: "Responses & Analytics",
    description: "Analyze responses and generate insights from your data.",
    articles: [
      { title: "Viewing survey responses", slug: "view-responses" },
      { title: "Understanding analytics dashboards", slug: "analytics-dashboard" },
      { title: "Exporting response data", slug: "export-data" },
      { title: "Response filtering and segmentation", slug: "filter-responses" },
    ],
  },
  {
    id: "account-billing",
    icon: CreditCard,
    title: "Account & Billing",
    description: "Manage your account settings and subscription.",
    articles: [
      { title: "Upgrading your plan", slug: "upgrade-plan" },
      { title: "Managing team members", slug: "team-management" },
      { title: "Billing and invoices", slug: "billing" },
      { title: "Cancellation and refunds", slug: "cancellation" },
    ],
  },
  {
    id: "troubleshooting",
    icon: AlertTriangle,
    title: "Troubleshooting",
    description: "Solutions to common issues and problems.",
    articles: [
      { title: "Document upload issues", slug: "upload-issues" },
      { title: "AI extraction not working", slug: "extraction-issues" },
      { title: "Survey not loading", slug: "survey-loading" },
      { title: "Browser compatibility", slug: "browser-support" },
    ],
  },
];

// Popular articles with proper routing info
const popularArticles = [
  { title: "Creating your first survey", category: "Getting Started", categoryId: "getting-started", slug: "first-survey" },
  { title: "Supported document formats", category: "Survey Creation", categoryId: "survey-creation", slug: "supported-formats" },
  { title: "Sharing via link", category: "Distribution", categoryId: "distribution", slug: "share-link" },
  { title: "Exporting response data", category: "Responses & Analytics", categoryId: "responses-analytics", slug: "export-data" },
];

export default function Help() {
  useDocumentMeta({
    title: 'Help Center',
    description: 'Find answers to common questions about SurviAI. Browse guides on survey creation, distribution, and analytics.'
  });

  const [searchQuery, setSearchQuery] = useState("");

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main>
        {/* Hero Section with Search */}
        <Section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-primary/5 to-background">
          <Container>
            <AnimatedSection className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                How can we <span className="text-gradient">help</span> you?
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Search our knowledge base or browse categories below
              </p>
              
              {/* Search Bar */}
              <div className="relative max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search for help articles..."
                  className="pl-12 pr-4 py-6 text-lg"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </AnimatedSection>
          </Container>
        </Section>

        {/* Quick Links */}
        <Section className="py-8 border-b border-border">
          <Container>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button variant="outline" size="sm">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Contact Support
                </Button>
              </Link>
              <a href="/#faq">
                <Button variant="outline" size="sm">
                  <BookOpen className="h-4 w-4 mr-2" />
                  View FAQ
                </Button>
              </a>
              <a href="mailto:support@survi.ai">
                <Button variant="outline" size="sm">
                  <Mail className="h-4 w-4 mr-2" />
                  Email Us
                </Button>
              </a>
            </div>
          </Container>
        </Section>

        {/* Help Categories */}
        <Section>
          <Container>
            <AnimatedSection className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Browse by Category
              </h2>
              <p className="text-muted-foreground">
                Find answers organized by topic
              </p>
            </AnimatedSection>
            
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {helpCategories.map((category) => (
                <motion.div key={category.id} variants={staggerItem}>
                  <Card className="h-full hover-lift">
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <category.icon className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{category.title}</CardTitle>
                          <CardDescription>{category.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {category.articles.map((article, index) => (
                          <li key={index}>
                            <Link 
                              to={`/help/${category.id}/${article.slug}`}
                              className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors group"
                            >
                              <ChevronRight className="h-4 w-4 mr-1 group-hover:translate-x-0.5 transition-transform" />
                              {article.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </Container>
        </Section>

        {/* Popular Articles */}
        <Section background="muted">
          <Container>
            <AnimatedSection className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
                Popular Articles
              </h2>
              <div className="space-y-3">
                {popularArticles.map((article, index) => (
                  <Link
                    key={index}
                    to={`/help/${article.categoryId}/${article.slug}`}
                    className="flex items-center justify-between p-4 bg-background rounded-lg border hover:border-primary/50 hover:shadow-sm transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <BookOpen className="h-5 w-5 text-primary" />
                      <span className="font-medium text-foreground">{article.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{article.category}</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </Link>
                ))}
              </div>
            </AnimatedSection>
          </Container>
        </Section>

        {/* Still Need Help CTA */}
        <Section>
          <Container>
            <AnimatedSection className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Still need help?
              </h2>
              <p className="text-muted-foreground mb-6">
                Our support team is available Monday through Friday, 9AM-6PM EST. 
                We typically respond within 24 hours.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg">
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Contact Support
                  </Button>
                </Link>
                <a href="mailto:support@survi.ai">
                  <Button size="lg" variant="outline">
                    <Mail className="mr-2 h-4 w-4" />
                    support@survi.ai
                  </Button>
                </a>
              </div>
            </AnimatedSection>
          </Container>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
